import { apiFetch } from './apiClient.js';

export type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
export type TemplateType =
  | 'standard'
  | 'carousel'
  | 'limited_time_offer'
  | 'media';
export type TemplateStatus = 'approved' | 'pending' | 'rejected' | 'draft';

export interface OutboundTemplate {
  id: string;
  tenantId: string;
  connectorId: string;
  name: string;
  templateName: string;
  language: string;
  category: TemplateCategory;
  templateType: TemplateType;
  status: TemplateStatus;
  bodyText: string;
  placeholders: string[];
  components?: Array<Record<string, unknown>>;
  providerTemplateId?: string;
  externalId?: string;
  namespace?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface OutboundTemplateCreate {
  connectorId: string;
  name: string;
  templateName: string;
  language: string;
  category: TemplateCategory;
  templateType?: TemplateType;
  status?: TemplateStatus;
  bodyText: string;
  placeholders?: string[];
  components?: Array<Record<string, unknown>>;
  providerTemplateId?: string;
  externalId?: string;
  namespace?: string;
}

export type OutboundTemplatePatch = Partial<
  Pick<
    OutboundTemplate,
    | 'name'
    | 'templateName'
    | 'language'
    | 'category'
    | 'templateType'
    | 'status'
    | 'bodyText'
    | 'placeholders'
    | 'components'
    | 'providerTemplateId'
    | 'externalId'
    | 'namespace'
  >
>;

export const LANGUAGE_REGEX = /^[a-z]{2}(-[A-Z]{2})?$/;
export const TEMPLATE_NAME_REGEX = /^[a-z0-9_]+$/;

interface ListTemplatesResponse {
  items: OutboundTemplate[];
}

export async function listTemplates(
  tenantId: string,
  opts: { includeArchived?: boolean; connectorId?: string } = {},
): Promise<OutboundTemplate[]> {
  const params: string[] = [];
  if (opts.includeArchived) params.push('includeArchived=true');
  if (opts.connectorId) params.push(`connectorId=${encodeURIComponent(opts.connectorId)}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  const res = await apiFetch<ListTemplatesResponse | OutboundTemplate[]>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/templates${qs}`,
  );
  return Array.isArray(res) ? res : res.items;
}

export async function getTemplate(
  tenantId: string,
  templateId: string,
): Promise<OutboundTemplate> {
  return apiFetch<OutboundTemplate>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}`,
  );
}

export async function createTemplate(
  tenantId: string,
  body: OutboundTemplateCreate,
): Promise<OutboundTemplate> {
  return apiFetch<OutboundTemplate>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/templates`,
    { method: 'POST', body },
  );
}

export async function updateTemplate(
  tenantId: string,
  templateId: string,
  patch: OutboundTemplatePatch,
  ifMatchVersion: number,
): Promise<OutboundTemplate> {
  return apiFetch<OutboundTemplate>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}`,
    {
      method: 'PATCH',
      body: patch,
      headers: { 'If-Match': String(ifMatchVersion) },
    },
  );
}

export async function archiveTemplate(
  tenantId: string,
  templateId: string,
): Promise<OutboundTemplate> {
  return apiFetch<OutboundTemplate>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}/archive`,
    { method: 'POST' },
  );
}
